import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {type ChangeEvent, type FC, useState} from 'react';
import {type ModalDialogProps} from 'interfaces';
import axios from 'axios';
import {incrementDateString} from 'helpers/UnitsFormatHandler';
import FileDownload from 'js-file-download';
import {useGetPlan} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {useTranslation} from 'react-i18next';
import Alert from 'helpers/Alert';
import {getBaseUrl} from '../../utils/runtimeVars';

declare type supportedFileFormats = 'excel' | 'csv';
declare type groupByAlternavies = 'none' | 'week' | 'month';

const DownloadHarvestReportDialog: FC<ModalDialogProps<any, any>> = ({dialogProps}) => {
  const [groupBy, setGroupBy] = useState<groupByAlternavies>('none');
  const [fileFormat, setFileFormat] = useState<supportedFileFormats>('excel');
  const [days, setDays] = useState<number | undefined>(28);
  const [loading, setLoading] = useState(false);
  const {planId} = useParseURL();

  const {data: plan} = useGetPlan({resourceId: planId});
  const {t} = useTranslation('common');

  const handleChangeGroupBy = (event: ChangeEvent<HTMLInputElement>) => {
    setGroupBy((event.target as HTMLInputElement).value as groupByAlternavies);
  };

  const handleChangeFileFormat = (event: ChangeEvent<HTMLInputElement>) => {
    setFileFormat(event.target.value as supportedFileFormats);
  };

  const handleClose = (event: any) => {
    dialogProps.onClose && dialogProps.onClose(event, 'escapeKeyDown');
  };

  const handleSubmit = (event: any) => {
    if (!plan || !days) {
      return;
    }
    setLoading(true);
    downloadHarvestReport({
      planId: plan.id,
      planName: plan.name,
      startDate: plan.startDate,
      endDate: incrementDateString(plan.startDate, days),
      groupBy,
      fileFormat,
    })
      .catch((error) => {
        Alert.error(error);
      })
      .finally(() => {
        setLoading(false);
        handleClose(event);
      });
  };

  return (
    <Dialog {...dialogProps} PaperProps={{elevation: 1, sx: {width: 340}}}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {t('downloadHarvestReportTitle')}
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <TextField
            type="tel"
            margin="normal"
            label={t('downloadHarvestReportDaysLable')}
            variant="outlined"
            value={days && days > 0 ? days : ''}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              setDays(value);
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{t('downloadHarvestReportDaysSuffix')}</InputAdornment>,
            }}
            InputLabelProps={{shrink: true}}
          />
          <FormControl>
            <FormLabel>{t('downloadHarvestReportGroupBy')}</FormLabel>
            <RadioGroup row value={groupBy} onChange={handleChangeGroupBy}>
              <FormControlLabel value="none" control={<Radio />} label={t('downloadHarvestReportGroupNone')} />
              <FormControlLabel value="week" control={<Radio />} label={t('downloadHarvestReportGroupWeek')} />
              <FormControlLabel value="month" control={<Radio />} label={t('downloadHarvestReportGroupMonth')} />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>{t('downloadHarvestReportFileFormat')}</FormLabel>
            <RadioGroup row value={fileFormat} onChange={handleChangeFileFormat}>
              <FormControlLabel value="excel" control={<Radio />} label="Excel" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={!days}
          autoFocus
          variant="contained"
          onClick={handleSubmit}
          endIcon={<DownloadIcon />}
          loading={loading}
          loadingPosition="end"
        >
          {t('downloadHarvestReportDownload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BASE_URL = getBaseUrl('');

const downloadHarvestReport = async ({
  planId,
  planName,
  startDate,
  endDate,
  groupBy,
  fileFormat,
}: {
  planId: string;
  planName: string;
  startDate: string;
  endDate: string;
  groupBy: groupByAlternavies;
  fileFormat: supportedFileFormats;
}): Promise<void> => {
  const fileName = getFileName(planName, fileFormat);
  const request = getFile(planId, startDate, endDate, groupBy, fileFormat);
  await request.then(({data}) => {
    FileDownload(data, fileName);
  });
};

const getFileName = (planName: string, fileFormat: supportedFileFormats) => {
  const extension = fileFormat === 'csv' ? '.csv' : '.xlsx';
  return `${planName} - Harvest Report${extension}`;
};

const getFile = async (
  planId: string,
  startDate: string,
  endDate: string,
  groupBy: groupByAlternavies,
  fileFormat: supportedFileFormats,
) => {
  const groupByQuery = groupBy === 'none' ? '' : `&groupBy=${groupBy}`;

  const request = axios.request({
    url: `${BASE_URL}/plans/${planId}/harvestReport?fileFormat=${fileFormat}&startDate=${startDate}&endDate=${endDate}${groupByQuery}`,
    method: 'GET',
    responseType: 'blob',
  });
  return await request;
};

export default DownloadHarvestReportDialog;
